import { ReactNode } from 'react'
import BusinessIcon from '@mui/icons-material/Business'
import ReceiptIcon from '@mui/icons-material/Receipt'
import AttachMoneyIcon from '@mui/icons-material/AttachMoney'
import HomeIcon from '@mui/icons-material/Home'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import PeopleIcon from '@mui/icons-material/People'
import StarIcon from '@mui/icons-material/Star'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import { ProdutoCategoria } from '@/types/produto'

/**
 * Catálogo único das categorias de produto — rótulo, ícone e cor.
 *
 * <p>Existia em triplicata: `ProdutosListPage`, `ProdutoFormPage` e `ProdutoDetailPage`
 * mantinham cada uma sua lista, então acrescentar uma categoria exigia **cinco** edições
 * espalhadas, e esquecer uma fazia a categoria aparecer sem nome ou sem ícone na tela.
 *
 * <p>Os valores espelham o enum `CategoriaProduto` do backend. Ao acrescentar lá,
 * acrescente aqui — o tipo `ProdutoCategoria` faz o compilador cobrar, porque o
 * `Record` abaixo é exaustivo.
 */
interface CategoriaProdutoInfo {
  value: ProdutoCategoria
  label: string
  icon: ReactNode
  color: string
}

/** Exaustivo por construção: falta uma categoria e o `tsc` acusa. */
const INFO: Record<ProdutoCategoria, Omit<CategoriaProdutoInfo, 'value'>> = {
  VENDOR_FINANCE:       { label: 'Vendor Finance',         icon: <BusinessIcon />,    color: '#6B7280' },
  RISCO_SACADO:         { label: 'Risco Sacado',           icon: <ReceiptIcon />,     color: '#6C3483' },
  CAPITAL_GIRO:         { label: 'Capital de Giro',        icon: <AttachMoneyIcon />, color: '#1E8449' },
  HOME_EQUITY:          { label: 'Home Equity',            icon: <HomeIcon />,        color: '#7D6608' },
  CESSAO_RECEBIVEIS:    { label: 'Antecipação de Recebíveis', icon: <TrendingUpIcon />, color: '#1A5276' },
  CONSIGNADO_PRIVADO:   { label: 'Consignado Privado',     icon: <PeopleIcon />,      color: '#922B21' },
  COMISSAO_INCENTIVO:   { label: 'Campanha de Incentivo',  icon: <StarIcon />,        color: '#D35400' },
  SACADO360:            { label: 'Sacado360',              icon: <ReceiptIcon />,     color: '#117864' },
  CAPITAL_GIRO_CAPEX:   { label: 'Capital de Giro CAPEX',  icon: <AttachMoneyIcon />, color: '#196F3D' },
  DUPLICATA_ESCRITURAL: { label: 'Duplicata Escritural',   icon: <ReceiptIcon />,     color: '#5B2C6F' },
  SACADOR360:           { label: 'Sacador360',             icon: <ReceiptIcon />,     color: '#0E6655' },
  OUTRA_NECESSIDADE:    { label: 'Outra necessidade',      icon: <HelpOutlineIcon />, color: '#566573' },
}

/**
 * Categorias que existem no catálogo mas NÃO se cadastram nem se filtram (AN-PP1 §3.2):
 * `OUTRA_NECESSIDADE` só tem o produto `OTHER_NEED`, criado pela migração — a Nova Reserva o
 * oferece como opção especial, e ele fica fora de "Produtos e Materiais". Rótulo mantido para
 * exibição.
 */
export const CATEGORIAS_ESPECIAIS: ReadonlySet<ProdutoCategoria> = new Set<ProdutoCategoria>([
  'OUTRA_NECESSIDADE',
])

/**
 * Categorias RETIRADAS do catálogo pela política vigente (Políticas Vigentes por Produto,
 * 25/10/2026, seção 8: Duplicata Escritural). O rótulo/ícone/cor continuam em `INFO` para
 * exibir dado histórico (produto inativo, reserva ou snapshot antigo), mas a categoria fica
 * FORA de filtros, selects e seletores — o backend recusa criar/editar/reativar (422).
 */
export const CATEGORIAS_RETIRADAS: ReadonlySet<ProdutoCategoria> = new Set<ProdutoCategoria>([
  'DUPLICATA_ESCRITURAL',
])

export const categoriaRetirada = (categoria: string): boolean =>
  CATEGORIAS_RETIRADAS.has(categoria as ProdutoCategoria)

/** Categorias oferecidas em filtros e selects, na ordem de declaração. Sem as retiradas nem as especiais. */
export const CATEGORIAS_PRODUTO: CategoriaProdutoInfo[] = (Object.keys(INFO) as ProdutoCategoria[])
  .filter((value) => !CATEGORIAS_RETIRADAS.has(value) && !CATEGORIAS_ESPECIAIS.has(value))
  .map((value) => ({ value, ...INFO[value] }))

/** Rótulo legível. Devolve o próprio código se a categoria for desconhecida — assim uma
 *  categoria nova vinda do backend aparece como código, e não como espaço em branco. */
export const rotuloCategoria = (categoria: string): string =>
  INFO[categoria as ProdutoCategoria]?.label ?? categoria

export const iconeCategoria = (categoria: string): ReactNode =>
  INFO[categoria as ProdutoCategoria]?.icon ?? <BusinessIcon />

export const corCategoria = (categoria: string): string =>
  INFO[categoria as ProdutoCategoria]?.color ?? '#6B7280'
